import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import DeckVisualView from './DeckVisualView';
import DropZone from './DropZone';
import { CheckboxCell, GainLossCell, FinishCell, ConditionCell, NameCell } from './TableCells';
import { VIEW_MODES } from '../../helpers/viewModeConstants';

const TABLE_ROW_HEIGHT = 46;
const TABLE_TOP_OFFSET = 250;

const { Column, HeaderCell, Cell } = Table;

const DeckContent = ({
  viewMode,
  cardLoading,
  tableData,
  sortedData,
  groupedData,
  tableGroupedData,
  sortColumn,
  sortType,
  displaySortColumn,
  columns,
  navigate,
  onRowClick,
  onSortColumn,
  onCardClick,
  onCardDrop,
  validationHighlights,
  collection,
  preferences,
  dragOverActive,
  resolvingDrop,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  COLORS,
  SPACING,
  selectedIds,
  onSelectAll,
  onToggleSelect,
}) => {
  const dropZoneStyles = {
    position: 'relative',
    minHeight: window.innerHeight - TABLE_TOP_OFFSET,
  };

  if (tableData.length === 0 && !cardLoading) {
    return (
      <DropZone
        dragOverActive={dragOverActive}
        resolvingDrop={resolvingDrop}
        COLORS={COLORS}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={dropZoneStyles}
      >
        <div
          style={{
            textAlign: 'center',
            padding: `60px 0`,
            color: COLORS.muted,
          }}
        >
          <p style={{ fontSize: 18, marginBottom: 8 }}>No cards in this collection</p>
          <p>Add cards from card pages to start building your collection.</p>
          <p style={{ fontSize: 12, marginTop: 16, color: '#666' }}>💡 Drag card images from EDHREC, TCGPlayer, or CardKingdom to add cards</p>
        </div>
      </DropZone>
    );
  }

  if (viewMode === VIEW_MODES.VISUAL) {
    return (
      <DropZone
        dragOverActive={dragOverActive}
        resolvingDrop={resolvingDrop}
        COLORS={COLORS}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={dropZoneStyles}
      >
        <DeckVisualView
          sortedData={sortedData}
          groupedData={groupedData}
          navigate={navigate}
          onCardClick={onCardClick}
          onCardDrop={onCardDrop}
          cardDisplaySize={preferences.card_display_size}
        />
      </DropZone>
    );
  }

  return (
    <DropZone
      dragOverActive={dragOverActive}
      resolvingDrop={resolvingDrop}
      COLORS={COLORS}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={dropZoneStyles}
    >
      <Table
        loading={cardLoading}
        data={tableGroupedData}
        height={window.innerHeight - TABLE_TOP_OFFSET}
        onRowClick={onRowClick}
        virtualized
        rowHeight={TABLE_ROW_HEIGHT}
        sortColumn={displaySortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        rowClassName={(rowData) => {
          const { errorMap, warningMap } = validationHighlights;
          const scryfallId = rowData?.scryfall_id;
          const isCommander = collection?.deck_type === 'COMMANDER' && rowData?.is_commander;
          const isNotOwned = rowData?.is_not_owned;

          const notOwnedClass = isNotOwned ? ' row-not-owned' : '';
          if (isCommander) {
            return `commander-row${notOwnedClass}`;
          }
          if (scryfallId && errorMap[scryfallId]) {
            return `violation-error-row${notOwnedClass}`;
          }
          if (scryfallId && warningMap[scryfallId]) {
            return `violation-warning-row${notOwnedClass}`;
          }
          return isNotOwned ? 'row-not-owned' : '';
        }}
      >
        {columns.map(({ key, label, custom, sortKey, ...rest }) => (
          <Column {...rest} key={key}>
            {custom === 'checkbox'
              ? <HeaderCell style={{ padding: '6px 0', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={tableData.length > 0 && selectedIds.size === tableData.length}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < tableData.length;
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      onSelectAll();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </HeaderCell>
              : <HeaderCell>{label}</HeaderCell>}
            {custom === 'checkbox'
              ? <CheckboxCell selectedIds={selectedIds} onToggleSelect={onToggleSelect} />
              : custom === 'actions'
                ? <Cell dataKey={key} style={{ padding: '6px 0' }} />
                : custom
                  ? <GainLossCell dataKey={key} />
                  : key === 'name'
                    ? <NameCell dataKey={key} errorMap={validationHighlights.errorMap} warningMap={validationHighlights.warningMap} deckType={collection?.deck_type} />
                    : key === 'finish'
                      ? <FinishCell dataKey={key} />
                      : key === 'condition'
                        ? <ConditionCell dataKey={key} />
                        : <Cell dataKey={key} />}
          </Column>
        ))}
      </Table>
    </DropZone>
  );
};

DeckContent.propTypes = {
  viewMode: PropTypes.oneOf([VIEW_MODES.TABLE, VIEW_MODES.VISUAL]).isRequired,
  cardLoading: PropTypes.bool,
  tableData: PropTypes.array.isRequired,
  sortedData: PropTypes.array.isRequired,
  groupedData: PropTypes.array.isRequired,
  tableGroupedData: PropTypes.array.isRequired,
  sortColumn: PropTypes.string,
  sortType: PropTypes.string,
  displaySortColumn: PropTypes.string,
  columns: PropTypes.array.isRequired,
  navigate: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onSortColumn: PropTypes.func.isRequired,
  onCardClick: PropTypes.func.isRequired,
  onCardDrop: PropTypes.func.isRequired,
  validationHighlights: PropTypes.object.isRequired,
  collection: PropTypes.object,
  preferences: PropTypes.object.isRequired,
  dragOverActive: PropTypes.bool,
  resolvingDrop: PropTypes.bool,
  handleDragOver: PropTypes.func.isRequired,
  handleDragEnter: PropTypes.func.isRequired,
  handleDragLeave: PropTypes.func.isRequired,
  handleDrop: PropTypes.func.isRequired,
  COLORS: PropTypes.object.isRequired,
  SPACING: PropTypes.object.isRequired,
  selectedIds: PropTypes.object.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onToggleSelect: PropTypes.func.isRequired,
};

DeckContent.defaultProps = {
  cardLoading: false,
  sortColumn: null,
  sortType: null,
  displaySortColumn: null,
  collection: null,
  dragOverActive: false,
  resolvingDrop: false,
};

export default DeckContent;
